/**
 * RTK Query API slice for user-facing contracting endpoints.
 * Separate from packagesApi.ts (admin) because it uses different base paths,
 * response types, and cache invalidation strategies.
 */
import { baseApi } from '@/store/api/baseApi';
import { ApiTags, ContractingPackageTagIds } from '@/store/api/tags';

import { ContractingDocumentStatus, SignatureIntent } from '@/utils/enums';

import type { ContractingProductType } from './packagesApi';

// --- Types ---

export interface ApprovalDetailResponse {
  id: number;
  companyName: string;
  action: string;
  side: 'BUYER' | 'SELLER' | null;
  created: string;
}

export interface DocumentDetailResponse {
  id: number;
  negotiationId: number;
  templateId: number | null;
  templateVersionId: number | null;
  templateName: string | null;
  uploadAndSignatureRequired: boolean;
  requiresRegulatoryData: boolean;
  documentStatus: ContractingDocumentStatus;
  signatureIntent: SignatureIntent | null;
  uploadedByCompanyName: string | null;
  hasFile: boolean;
  documentVersion: number;
  fileName: string;
  approvals: ApprovalDetailResponse[];
  created: string;
  updated: string;
  uploadedAt: string | null;
}

export interface ContractingNegotiationDetailResponse {
  id: number;
  buyerCompanyId: string;
  sellerCompanyId: string;
  buyerCompanyName: string;
  sellerCompanyName: string;
  viewerRole: 'BUYER' | 'SELLER' | null;
  projectName: string;
  productType: ContractingProductType;
  productTypeName: string;
  packageId: number | null;
  isBespoke: boolean;
  status: string;
  documents: DocumentDetailResponse[];
  supplementalDocumentsCount: number;
  created: string;
  updated: string;
}

export interface PackageSummaryResponse {
  id: number;
  name: string;
  description: string | null;
  productType: string;
  productTypeName: string;
  templateNames: string[];
  templateCount: number;
}

// --- Query param types ---

export interface GetContractingNegotiationDetailParams {
  id: number;
  companyId: string;
}

export interface GetUserPackagesParams {
  productType: ContractingProductType;
  companyId: string;
}

export interface SelectPackageParams {
  negotiationId: number;
  packageId: number;
  companyId: string;
}

export interface UploadDocumentParams {
  documentId: number;
  file: File;
  signatureIntent: SignatureIntent;
  companyId: string;
  negotiationId: number;
  regulatoryData?: Record<string, string | number>;
}

export interface ReplaceDocumentParams {
  documentId: number;
  file: File;
  signatureIntent: SignatureIntent;
  companyId: string;
  negotiationId: number;
  regulatoryData?: Record<string, string | number>;
}

export interface RejectDocumentParams {
  documentId: number;
  companyId: string;
  negotiationId: number;
}

export interface ApproveDocumentParams {
  documentId: number;
  companyId: string;
  negotiationId: number;
}

export interface SignDocumentParams {
  documentId: number;
  companyId: string;
}

export interface SignDocumentResponse {
  signingUrl: string;
}

export interface DownloadDocumentParams {
  documentId: number;
  companyId: string;
  version?: number;
}

export interface DocumentDownloadResponse {
  url: string;
  filename: string;
  documentVersion: number;
}

/**
 * Response shape from the contracting execution terms endpoint.
 * Differs from the negotiations ExecutionTermsDto in field names.
 */
export type ContractingExecutionTermsDto = {
  id: number;
  negotiationId: number;
  fixedPrice: number;
  tenorStart: string;
  tenorMonths: number;
  reportingCounterparty: string;
  settlementPoint: string;
  settlementPointIndex: string;
  buyerFraction: number;
  nameplateCapacity: number;
  offeredCapacity: number;
  negativePricingMethod: string | null;
  applicableRecProgram: string | null;
  applicableTrackingSystem: string | null;
  recDeliveryMethodology: string | null;
  performanceGuaranteeMethod: string | null;
  buyerPostCodCollateral: string | null;
  sellerPostCodCollateral: string | null;
  created: string;
  updated: string;
};

export interface GetContractingExecutionTermsParams {
  negotiationId: number;
  companyId: string;
}

export interface CheckSigningStatusParams {
  documentId: number;
  companyId: string;
}

export interface SigningStatusResponse {
  updated: boolean;
}

// --- API Slice ---

const BASE_URL = '/api/v1/contracting';

/** Appends an optional JSON value as a multipart Blob part to FormData. */
function appendJsonPart(
  formData: FormData,
  name: string,
  value: Record<string, unknown> | undefined
): void {
  if (value) {
    formData.append(
      name,
      new Blob([JSON.stringify(value)], { type: 'application/json' })
    );
  }
}

export const contractingUserApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getContractingNegotiationDetail: build.query<
      ContractingNegotiationDetailResponse,
      GetContractingNegotiationDetailParams
    >({
      query: ({ id, companyId }) => ({
        url: `${BASE_URL}/negotiations/${id}`,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      // Sort documents by id for stable render order (backend doesn't guarantee ordering)
      transformResponse: (response: ContractingNegotiationDetailResponse) => ({
        ...response,
        documents: [...response.documents].sort((a, b) => a.id - b.id),
      }),
      providesTags: (result, error, { id }) => [
        { type: ApiTags.CONTRACTING_NEGOTIATIONS, id },
      ],
    }),

    getUserPackages: build.query<
      PackageSummaryResponse[],
      GetUserPackagesParams
    >({
      query: ({ productType, companyId }) => ({
        url: `${BASE_URL}/packages`,
        params: { productType },
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      providesTags: (result, error, { productType }) => [
        {
          type: ApiTags.CONTRACTING_PACKAGES,
          id: `${ContractingPackageTagIds.LIST}-${productType}`,
        },
      ],
    }),

    selectPackage: build.mutation<void, SelectPackageParams>({
      query: ({ negotiationId, packageId, companyId }) => ({
        url: `${BASE_URL}/negotiations/${negotiationId}/select-package`,
        method: 'POST',
        body: { packageId },
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      invalidatesTags: (result, error, { negotiationId }) => [
        { type: ApiTags.CONTRACTING_NEGOTIATIONS, id: negotiationId },
      ],
    }),

    uploadDocument: build.mutation<void, UploadDocumentParams>({
      query: ({
        documentId,
        file,
        signatureIntent,
        companyId,
        regulatoryData,
      }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('signatureIntent', signatureIntent);
        appendJsonPart(formData, 'regulatoryData', regulatoryData);
        return {
          url: `${BASE_URL}/documents/${documentId}/upload`,
          method: 'POST',
          body: formData,
          headers: { 'X-Company-Id': companyId },
        };
      },
      invalidatesTags: (result, error, { negotiationId }) =>
        error
          ? []
          : [{ type: ApiTags.CONTRACTING_NEGOTIATIONS, id: negotiationId }],
    }),

    replaceDocument: build.mutation<void, ReplaceDocumentParams>({
      query: ({
        documentId,
        file,
        signatureIntent,
        companyId,
        regulatoryData,
      }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('signatureIntent', signatureIntent);
        appendJsonPart(formData, 'regulatoryData', regulatoryData);
        return {
          url: `${BASE_URL}/documents/${documentId}/replace`,
          method: 'POST',
          body: formData,
          headers: { 'X-Company-Id': companyId },
        };
      },
      invalidatesTags: (result, error, { negotiationId }) =>
        error
          ? []
          : [{ type: ApiTags.CONTRACTING_NEGOTIATIONS, id: negotiationId }],
    }),

    rejectDocument: build.mutation<void, RejectDocumentParams>({
      query: ({ documentId, companyId }) => ({
        url: `${BASE_URL}/documents/${documentId}/reject`,
        method: 'POST',
        headers: { 'X-Company-Id': companyId },
      }),
      invalidatesTags: (result, error, { negotiationId }) =>
        error
          ? []
          : [{ type: ApiTags.CONTRACTING_NEGOTIATIONS, id: negotiationId }],
    }),

    approveDocument: build.mutation<void, ApproveDocumentParams>({
      query: ({ documentId, companyId }) => ({
        url: `${BASE_URL}/documents/${documentId}/approve`,
        method: 'POST',
        headers: { 'X-Company-Id': companyId },
      }),
      invalidatesTags: (result, error, { negotiationId }) =>
        error
          ? []
          : [{ type: ApiTags.CONTRACTING_NEGOTIATIONS, id: negotiationId }],
    }),

    getDocumentDownload: build.query<
      DocumentDownloadResponse,
      DownloadDocumentParams
    >({
      query: ({ documentId, companyId, version }) => ({
        url: `${BASE_URL}/documents/${documentId}/download`,
        params: version === undefined ? undefined : { version },
        headers: { 'X-Company-Id': companyId },
      }),
      keepUnusedDataFor: 0,
    }),

    signDocument: build.mutation<SignDocumentResponse, SignDocumentParams>({
      query: ({ documentId, companyId }) => ({
        url: `${BASE_URL}/documents/${documentId}/sign`,
        method: 'POST',
        headers: { 'X-Company-Id': companyId },
      }),
      // No cache invalidation — status changes are driven by the DocuSign webhook + polling
    }),

    getContractingExecutionTerms: build.query<
      ContractingExecutionTermsDto,
      GetContractingExecutionTermsParams
    >({
      query: ({ negotiationId, companyId }) => ({
        url: `${BASE_URL}/negotiations/${negotiationId}/execution-terms`,
        headers: { 'X-Company-Id': companyId },
      }),
      providesTags: (result, error, { negotiationId }) => [
        { type: ApiTags.CONTRACTING_NEGOTIATIONS, id: negotiationId },
      ],
    }),

    checkSigningStatus: build.mutation<
      SigningStatusResponse,
      CheckSigningStatusParams
    >({
      query: ({ documentId, companyId }) => ({
        url: `${BASE_URL}/documents/${documentId}/check-signing-status`,
        method: 'POST',
        headers: { 'X-Company-Id': companyId },
      }),
      // Invalidate the negotiation cache when the status was updated so
      // the DocumentSection refetches with the new status immediately.
      invalidatesTags: result => {
        if (result?.updated) {
          return [{ type: ApiTags.CONTRACTING_NEGOTIATIONS }];
        }
        return [];
      },
    }),
  }),
});

export const {
  useGetContractingNegotiationDetailQuery,
  useGetUserPackagesQuery,
  useSelectPackageMutation,
  useUploadDocumentMutation,
  useReplaceDocumentMutation,
  useRejectDocumentMutation,
  useApproveDocumentMutation,
  useSignDocumentMutation,
  useLazyGetDocumentDownloadQuery,
  useGetContractingExecutionTermsQuery,
  useCheckSigningStatusMutation,
} = contractingUserApi;
